import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { buttonText, buttonTextLarge } from "@/lib/typography";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600",
        destructive: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600",
        outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-600",
        secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus-visible:ring-gray-600",
        ghost: "text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-600",
        link: "text-blue-600 underline-offset-4 hover:underline focus-visible:ring-blue-600",
        // Professional variants
        primary: "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600 shadow-sm",
        success: "bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-600 shadow-sm",
        warning: "bg-yellow-600 text-white hover:bg-yellow-700 focus-visible:ring-yellow-600 shadow-sm",
        info: "bg-blue-500 text-white hover:bg-blue-600 focus-visible:ring-blue-500 shadow-sm",
        neutral: "bg-gray-600 text-white hover:bg-gray-700 focus-visible:ring-gray-600 shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
